import { useState } from 'react';
import type { ElementBoxAttrs } from './elementRenderContext';
import { useListingWords } from './listingWords';
import './elementShare.css';

// A button that shares the page it is on, as Sleek's product page has one: the phone's own share sheet
// where there is one, and elsewhere the address copied, with "Link copied" said out loud and shown.

/** How long "Link copied" stays before the button reads as itself again. */
const COPIED_MS = 2000;

// Hands the page's address to the share sheet, else copies it; says whether it was copied.
async function sharePage(): Promise<boolean> {
  const page = { title: document.title, url: window.location.href };
  if (navigator.share) {
    await navigator.share(page).catch(() => undefined);
    return false;
  }
  await navigator.clipboard?.writeText(page.url);
  return true;
}

// Draws the share button with the merchant's words and icon, and says so when the link was copied.
export const ShareButton: React.FC<{ box: ElementBoxAttrs; words: React.ReactNode }> = ({ box, words }) => {
  const [copied, setCopied] = useState(false);
  const said = useListingWords();
  const press = async () => {
    if (!(await sharePage().catch(() => false))) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), COPIED_MS);
  };
  return (
    <button {...box} type="button" onClick={press}>
      {words}
      <span className="xe-share-said" role="status">{copied ? said.copied : ''}</span>
    </button>
  );
};
